import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Edit2, Mail, Phone, Plus, Power, Save, Search, Scissors, Trash2, X } from 'lucide-react';
import { useNotify } from '../components/NotificationProvider';
import {
  actualizarManicurista,
  cambiarEstadoManicurista,
  crearManicurista,
  eliminarManicurista,
  obtenerManicuristas
} from '../api/manicuristas';
import { obtenerUsuarios } from '../api/usuarios';
import { getPhoneDigits, isValidEmail, isValidPhone } from '../utils/helpers';

const emptyForm = {
  Nombre: '',
  Telefono: '',
  Email: '',
  Activa: true,
  UsuarioId: ''
};

const normalize = (value) => String(value || '').trim();
const normalizeName = (value) => normalize(value).replace(/\s+/g, ' ').toLowerCase();
const isActive = (value) => value === true || value === 1 || value === '1' || String(value).toLowerCase() === 'true';

const AdminManicuristas = ({ user }) => {
  const { success: notifySuccess, error: notifyError, info: notifyInfo } = useNotify();
  const [manicuristas, setManicuristas] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [usuarios, setUsuarios] = useState([]);

  const cargarManicuristas = async () => {
    try {
      setLoading(true);
      const data = await obtenerManicuristas(user);
      setManicuristas(Array.isArray(data) ? data : []);
    } catch (err) {
      notifyError(err.message || 'Error al cargar manicuristas', { duration: 4500 });
    } finally {
      setLoading(false);
    }
  };

  const cargarUsuarios = async () => {
    try {
      const data = await obtenerUsuarios({ actor: user });
      setUsuarios(Array.isArray(data) ? data : []);
    } catch (err) {
      // No bloquear la página si falla la carga de usuarios; solo no mostrar select
      setUsuarios([]);
    }
  };

  useEffect(() => {
    cargarManicuristas();
    cargarUsuarios();
  }, []);

  const filtradas = useMemo(() => {
    const q = normalize(searchTerm).toLowerCase();
    if (!q) return manicuristas;
    return manicuristas.filter((item) =>
      [item.Nombre, item.Telefono, item.Email]
        .some((value) => normalize(value).toLowerCase().includes(q))
    );
  }, [manicuristas, searchTerm]);

  const findDuplicate = (field, value) => {
    const editingId = editing?.Id == null ? null : Number(editing.Id);
    return manicuristas.find((item) => {
      if (editingId !== null && Number(item.Id) === editingId) return false;

      if (field === 'Nombre') {
        return normalizeName(item.Nombre) === normalizeName(value);
      }
      if (field === 'Telefono') {
        const current = getPhoneDigits(item.Telefono);
        const next = getPhoneDigits(value);
        const currentLocal = current.startsWith('56') ? current.slice(2) : current;
        const nextLocal = next.startsWith('56') ? next.slice(2) : next;
        return currentLocal && nextLocal && currentLocal === nextLocal;
      }
      if (field === 'Email') {
        return normalize(item.Email).toLowerCase() === normalize(value).toLowerCase();
      }
      return false;
    });
  };

  const validateForm = () => {
    const nextErrors = {};
    const nombre = normalize(form.Nombre);
    const telefono = normalize(form.Telefono);
    const email = normalize(form.Email);

    if (!nombre) {
      nextErrors.Nombre = 'Nombre requerido';
    } else if (nombre.length < 3 || !/[a-zA-Z]/.test(nombre)) {
      nextErrors.Nombre = 'Ingresa un nombre valido';
    } else if (findDuplicate('Nombre', nombre)) {
      nextErrors.Nombre = 'Ese nombre ya esta registrado';
    }

    if (!telefono) {
      nextErrors.Telefono = 'Telefono requerido';
    } else if (!isValidPhone(telefono)) {
      nextErrors.Telefono = 'Ingresa un telefono valido';
    } else if (findDuplicate('Telefono', telefono)) {
      nextErrors.Telefono = 'Ese telefono ya esta registrado';
    }

    if (!email) {
      nextErrors.Email = 'Correo requerido';
    } else if (!isValidEmail(email)) {
      nextErrors.Email = 'Ingresa un correo valido';
    } else if (findDuplicate('Email', email)) {
      nextErrors.Email = 'Ese correo ya esta registrado';
    }

    setErrors(nextErrors);
    return nextErrors;
  };

  const handleFormChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleUsuarioChange = (value) => {
    const usuarioId = value || '';
    if (!usuarioId) {
      setForm(prev => ({ ...prev, UsuarioId: '' }));
      return;
    }
    const selected = usuarios.find((u) => String(u.Id) === String(usuarioId));
    if (!selected) {
      setForm(prev => ({ ...prev, UsuarioId: usuarioId }));
      return;
    }
    setForm(prev => ({
      ...prev,
      UsuarioId: usuarioId,
      Nombre: selected.NombreCompleto || selected.NombreUsuario || prev.Nombre,
      Telefono: selected.Telefono || prev.Telefono,
      Email: selected.Correo || prev.Email
    }));
    setErrors(prev => ({ ...prev, Nombre: '', Telefono: '', Email: '' }));
  };

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setErrors({});
    setModalOpen(true);
  };

  const openEdit = (manicurista) => {
    setEditing(manicurista);
    setForm({
      Nombre: manicurista.Nombre || '',
      Telefono: manicurista.Telefono || '',
      Email: manicurista.Email || '',
      Activa: isActive(manicurista.Activa),
      UsuarioId: manicurista.UsuarioId || ''
    });
    setErrors({});
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
    setForm(emptyForm);
    setErrors({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;

    const payload = {
      Nombre: normalize(form.Nombre),
      Telefono: normalize(form.Telefono),
      Email: normalize(form.Email),
      Activa: !!form.Activa,
      UsuarioId: form.UsuarioId || null
    };

    const validationErrors = validateForm();
    const firstError = Object.values(validationErrors).find(Boolean);
    if (firstError) {
      notifyError(firstError, { duration: 4000 });
      return;
    }

    try {
      setSaving(true);
      if (editing?.Id) {
        await actualizarManicurista(editing.Id, payload, user);
        notifySuccess('Manicurista actualizada', { duration: 3000 });
      } else {
        await crearManicurista(payload, user);
        notifySuccess('Manicurista agregada', { duration: 3000 });
      }
      closeModal();
      await cargarManicuristas();
    } catch (err) {
      notifyError(err.message || 'No se pudo guardar la manicurista', { duration: 4500 });
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (manicurista) => {
    try {
      setSaving(true);
      const wasActive = isActive(manicurista.Activa);
      const result = await cambiarEstadoManicurista(manicurista.Id, !wasActive, user);
      if (result?.manicurista) {
        setManicuristas((current) => current.map((item) => (
          item.Id === result.manicurista.Id
            ? { ...item, ...result.manicurista }
            : item
        )));
      }
      await cargarManicuristas();
      notifySuccess(wasActive ? 'Manicurista desactivada' : 'Manicurista activada', { duration: 3000 });
    } catch (err) {
      notifyError(err.message || 'No se pudo cambiar el estado', { duration: 4500 });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (manicurista) => {
    const usadas = Number(manicurista.ReservasAsociadas || 0);
    if (usadas > 0) {
      notifyInfo('No se puede eliminar: tiene reservas asociadas', { duration: 4000 });
      return;
    }

    try {
      setSaving(true);
      await eliminarManicurista(manicurista.Id, user);
      await cargarManicuristas();
      notifySuccess('Manicurista eliminada', { duration: 3000 });
    } catch (err) {
      notifyError(err.message || 'No se pudo eliminar la manicurista', { duration: 4500 });
    } finally {
      setSaving(false);
    }
  };

  const inputClass = (field) =>
    `w-full px-4 py-3 rounded-xl border-2 focus:outline-none ${
      errors[field]
        ? 'border-red-300 bg-red-50 focus:border-red-500'
        : 'border-gray-200 focus:border-pink-500'
    }`;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.45 }}
    >
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Manicuristas
          </h1>
          <p className="text-gray-600">Administra el equipo disponible para reservas</p>
        </div>
      </div>

      <div className="bg-white/90 rounded-2xl shadow-lg border border-pink-100 p-5">
        <div className="flex flex-col md:flex-row md:items-center gap-4 mb-5">
          <button
            type="button"
            onClick={openCreate}
            className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-bold text-white bg-gradient-to-r from-pink-500 to-purple-600"
          >
            <Plus className="w-5 h-5" />
            Agregar manicurista
          </button>

          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar manicurista..."
              className="w-full pl-12 pr-4 py-3 rounded-xl border-2 border-gray-200 focus:border-pink-500 focus:outline-none"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-pink-100 text-left text-purple-700">
                <th className="py-3 px-3 font-bold">Nombre</th>
                <th className="py-3 px-3 font-bold">Contacto</th>
                <th className="py-3 px-3 font-bold">Estado</th>
                <th className="py-3 px-3 font-bold">Reservas</th>
                <th className="py-3 px-3 font-bold text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} className="py-8 text-center text-gray-500">
                    Cargando...
                  </td>
                </tr>
              ) : filtradas.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-8 text-center text-gray-500">
                    Sin manicuristas
                  </td>
                </tr>
              ) : (
                filtradas.map((manicurista) => {
                  const activa = isActive(manicurista.Activa);
                  const reservas = Number(manicurista.ReservasAsociadas || 0);
                  const activas = Number(manicurista.ReservasActivas || 0);

                  return (
                    <tr key={manicurista.Id} className="border-b border-gray-100">
                      <td className="py-3 px-3">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-xl bg-pink-100 text-pink-600 flex items-center justify-center">
                            <Scissors className="w-4 h-4" />
                          </div>
                          <div className="min-w-0">
                            <span className="block font-semibold text-gray-900">{manicurista.Nombre}</span>
                            <span className={`block text-xs ${manicurista.UsuarioId ? 'text-green-600' : 'text-amber-600'}`}>
                              {manicurista.UsuarioNombre ? `Usuario: ${manicurista.UsuarioNombre}` : 'Sin usuario vinculado'}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-3 text-gray-600">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <Phone className="w-4 h-4 text-gray-400" />
                            <span>{manicurista.Telefono || '-'}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Mail className="w-4 h-4 text-gray-400" />
                            <span>{manicurista.Email || '-'}</span>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-3">
                        <span className={`inline-flex px-2 py-1 rounded-lg text-xs font-bold ${activa ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                          {activa ? 'Activa' : 'Inactiva'}
                        </span>
                      </td>
                      <td className="py-3 px-3 text-gray-600">
                        {reservas} total / {activas} activas
                      </td>
                      <td className="py-3 px-3">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => openEdit(manicurista)}
                            className="p-2 rounded-lg bg-blue-100 text-blue-700 hover:bg-blue-200"
                            title="Editar"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleToggle(manicurista)}
                            disabled={saving}
                            className={`p-2 rounded-lg disabled:opacity-50 ${activa ? 'bg-gray-100 text-gray-700 hover:bg-gray-200' : 'bg-green-100 text-green-700 hover:bg-green-200'}`}
                            title={activa ? 'Desactivar' : 'Activar'}
                          >
                            <Power className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(manicurista)}
                            disabled={saving || reservas > 0}
                            className="p-2 rounded-lg bg-red-100 text-red-700 hover:bg-red-200 disabled:opacity-40"
                            title="Eliminar"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <form
            onSubmit={handleSubmit}
            noValidate
            className="bg-white rounded-2xl w-full max-w-lg p-6 shadow-xl border border-purple-200"
          >
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-xl font-bold text-purple-700">
                {editing ? 'Editar manicurista' : 'Agregar manicurista'}
              </h2>
              <button
                type="button"
                onClick={closeModal}
                className="p-2 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200"
                title="Cerrar"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Vincular usuario registrado</label>
                <select
                  value={form.UsuarioId}
                  onChange={(e) => handleUsuarioChange(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-pink-500 focus:outline-none bg-white"
                >
                  <option value="">Sin usuario vinculado</option>
                  {usuarios.map((u) => {
                    const linked = manicuristas.find(
                      (m) => m.UsuarioId && String(m.UsuarioId) === String(u.Id) && String(m.Id) !== String(editing?.Id || '')
                    );
                    const roleLabel = String(u.Rol || '').toLowerCase() === 'super_admin'
                      ? 'Super admin'
                      : String(u.Rol || '').toLowerCase() === 'admin'
                        ? 'Manicurista'
                        : 'Cliente';
                    return (
                      <option key={u.Id} value={u.Id}>
                        {u.NombreUsuario} ({u.NombreCompleto || u.Correo || '-'}) — {roleLabel}{linked ? ' (ya vinculado)' : ''}
                      </option>
                    );
                  })}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Al seleccionar un usuario se rellenan sus datos y se vincula como manicurista.
                </p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Nombre</label>
                <input
                  type="text"
                  value={form.Nombre}
                  onChange={(e) => handleFormChange('Nombre', e.target.value)}
                  className={inputClass('Nombre')}
                  maxLength={100}
                  required
                  autoFocus
                  aria-invalid={!!errors.Nombre}
                />
                {errors.Nombre && <p className="text-red-500 text-sm mt-1">{errors.Nombre}</p>}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Telefono</label>
                  <input
                    type="tel"
                    value={form.Telefono}
                    onChange={(e) => handleFormChange('Telefono', e.target.value)}
                    className={inputClass('Telefono')}
                    maxLength={30}
                    placeholder="+56912345678"
                    inputMode="tel"
                    required
                    aria-invalid={!!errors.Telefono}
                  />
                  {errors.Telefono && <p className="text-red-500 text-sm mt-1">{errors.Telefono}</p>}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Correo</label>
                  <input
                    type="email"
                    value={form.Email}
                    onChange={(e) => handleFormChange('Email', e.target.value)}
                    className={inputClass('Email')}
                    maxLength={100}
                    placeholder="correo@ejemplo.cl"
                    required
                    aria-invalid={!!errors.Email}
                  />
                  {errors.Email && <p className="text-red-500 text-sm mt-1">{errors.Email}</p>}
                </div>
              </div>

              <label className="inline-flex items-center gap-2 text-sm font-semibold text-gray-700">
                <input
                  type="checkbox"
                  checked={!!form.Activa}
                  onChange={(e) => handleFormChange('Activa', e.target.checked)}
                  className="w-4 h-4 accent-pink-600"
                />
                Activa
              </label>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                type="button"
                onClick={closeModal}
                className="px-4 py-2 rounded-xl bg-gray-200 text-gray-700 hover:bg-gray-300"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50"
              >
                <Save className="w-4 h-4" />
                Guardar
              </button>
            </div>
          </form>
        </div>
      )}
    </motion.div>
  );
};

export default AdminManicuristas;
